import { useState } from 'react';
import api, { BACKEND_URL } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Star, ChevronRight, ChevronUp, ChevronDown, X, Plus, ImagePlus,
  Image, Maximize2, CheckCircle2
} from 'lucide-react';

const SKILL_IMAGE_LIBRARY = [
  { name: 'Bubbles', url: 'https://images.unsplash.com/photo-1627540458907-47a427507e20?w=400&h=400&fit=crop' },
  { name: 'Pool', url: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=400&h=400&fit=crop' },
  { name: 'Swimming', url: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=400&h=400&fit=crop' },
  { name: 'Splash', url: 'https://images.unsplash.com/photo-1600965962102-9d260a71890d?w=400&h=400&fit=crop' },
  { name: 'Float', url: 'https://images.unsplash.com/photo-1519315901367-f34ff9154487?w=400&h=400&fit=crop' },
  { name: 'Underwater', url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=400&fit=crop' },
  { name: 'Dive', url: 'https://images.unsplash.com/photo-1560089000-7433a4ebbd64?w=400&h=400&fit=crop' },
  { name: 'Jump', url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=400&fit=crop' },
];

export { SKILL_IMAGE_LIBRARY };

export function getSkillImageSrc(skill) {
  if (!skill.image) return null;
  if (skill.image_is_url || skill.image.startsWith('http')) return skill.image;
  return `${BACKEND_URL}/api/uploads/${skill.image}`;
}

export function SessionSkillGrid({
  session, sessionId, activeSkill, setActiveSkill, isParent, onFetchSession,
  onOpenFullscreen, onShowImageLibrary,
}) {
  const [newSkillName, setNewSkillName] = useState('');

  const completeSkill = async (skill) => {
    try {
      await api.put(`/sessions/${sessionId}/skills/${skill.skill_id}/complete`);
      toast.success(`${skill.name} completed!`);
      onFetchSession();
    } catch { toast.error('Failed to complete skill'); }
  };

  const uncompleteSkill = async (skill) => {
    try {
      await api.put(`/sessions/${sessionId}/skills/${skill.skill_id}/uncomplete`);
      toast.success(`${skill.name} marked as incomplete`);
      onFetchSession();
    } catch { toast.error('Failed to undo'); }
  };

  const addSkill = async () => {
    if (!newSkillName.trim() || !session) return;
    const newSkill = {
      skill_id: `skill_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      name: newSkillName, order: session.skills.length + 1, completed: false
    };
    try {
      await api.put(`/sessions/${sessionId}`, { skills: [...session.skills, newSkill] });
      setNewSkillName('');
      onFetchSession();
      toast.success('Skill added!');
    } catch { toast.error('Failed to add skill'); }
  };

  const removeSkill = async (skillId) => {
    if (!session) return;
    const updated = session.skills.filter(s => s.skill_id !== skillId).map((s, i) => ({ ...s, order: i + 1 }));
    try {
      await api.put(`/sessions/${sessionId}`, { skills: updated });
      onFetchSession();
    } catch { toast.error('Failed to remove skill'); }
  };

  const moveSkill = async (index, direction) => {
    if (!session) return;
    const skills = [...session.skills];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= skills.length) return;
    [skills[index], skills[newIndex]] = [skills[newIndex], skills[index]];
    const updated = skills.map((s, i) => ({ ...s, order: i + 1 }));
    try {
      await api.put(`/sessions/${sessionId}`, { skills: updated });
      onFetchSession();
    } catch { toast.error('Failed to reorder'); }
  };

  const handleSkillImageUpload = async (skillId, e) => {
    const file = e.target.files[0];
    if (!file || !session) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/skill-images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const updated = session.skills.map(s => s.skill_id === skillId ? { ...s, image: res.data.filename } : s);
      await api.put(`/sessions/${sessionId}`, { skills: updated });
      onFetchSession();
      toast.success('Skill image added!');
    } catch { toast.error('Failed to upload image'); }
  };

  const stepLabels = ['FIRST', 'THEN', 'NEXT', 'NEXT', 'NEXT', 'NEXT', 'NEXT', 'NEXT'];

  return (
    <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="visual-schedule">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>Visual Schedule</h2>
          {!isParent && session.status !== 'completed' && (
            <div className="flex gap-2 items-center">
              <Input className="w-40 rounded-xl text-sm" placeholder="Add skill" value={newSkillName} onChange={e => setNewSkillName(e.target.value)} data-testid="add-session-skill-input" onKeyDown={e => e.key === 'Enter' && addSkill()} />
              <Button size="sm" onClick={addSkill} className="rounded-xl bg-[#22D3EE]"><Plus className="w-4 h-4" /></Button>
            </div>
          )}
        </div>

        <div className="flex overflow-x-auto gap-3 pb-4 snap-x snap-mandatory" data-testid="skills-scroll">
          {session.skills.map((skill, idx) => {
            const isActive = activeSkill?.skill_id === skill.skill_id;
            const imgSrc = getSkillImageSrc(skill);
            return (
              <div key={skill.skill_id} className="flex items-center gap-2 snap-center flex-shrink-0">
                <div
                  onClick={() => setActiveSkill(skill)}
                  className={`schedule-step relative flex flex-col items-center justify-center w-28 h-36 sm:w-32 sm:h-40 rounded-2xl border-2 cursor-pointer touch-target-lg overflow-hidden
                    ${isActive ? 'active border-[#22D3EE] bg-[#ECFEFF] shadow-lg' : skill.completed ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid={`skill-card-${skill.skill_id}`}
                >
                  {imgSrc && (
                    <div className="absolute inset-0">
                      <img src={imgSrc} alt={skill.name} className="w-full h-full object-cover opacity-30" />
                    </div>
                  )}
                  <div className="relative z-10 flex flex-col items-center justify-center p-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#22D3EE] mb-1">{stepLabels[idx] || 'NEXT'}</span>
                    {imgSrc ? (
                      <img src={imgSrc} alt={skill.name} className="w-12 h-12 rounded-xl object-cover mb-1 border-2 border-white shadow-sm" />
                    ) : (
                      <span className="text-3xl mb-1">{skill.completed ? '⭐' : ['🫧','🦵','🏊','🔙','💪','🌊','😮','🤸'][idx % 8]}</span>
                    )}
                    <span className="text-xs font-bold text-center text-slate-700 px-1 leading-tight">{skill.name}</span>
                  </div>
                  {skill.completed && (
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!isParent && session.status === 'active') uncompleteSkill(skill); }}
                      className={`absolute -top-2 -right-2 w-7 h-7 bg-[#C4F017] rounded-full flex items-center justify-center shadow-md z-20 ${!isParent && session.status === 'active' ? 'cursor-pointer hover:bg-red-400 group' : ''}`}
                      title={!isParent && session.status === 'active' ? 'Click to undo' : 'Completed'}
                      data-testid={`undo-skill-${skill.skill_id}`}
                    >
                      <Star className="w-4 h-4 text-white fill-white group-hover:hidden" />
                      <X className="w-4 h-4 text-white hidden group-hover:block" />
                    </button>
                  )}
                  {!isParent && session.status === 'active' && !skill.completed && imgSrc && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenFullscreen(skill); }}
                      className="absolute top-1 right-1 w-6 h-6 bg-[#1E1B4B]/70 rounded-full flex items-center justify-center z-20 hover:bg-[#1E1B4B]"
                      data-testid={`fullscreen-skill-${skill.skill_id}`}
                    >
                      <Maximize2 className="w-3 h-3 text-white" />
                    </button>
                  )}
                  {!isParent && session.status !== 'completed' && !skill.completed && (
                    <>
                      <div className="absolute bottom-1 right-1 flex gap-1 z-20">
                        <label className="w-6 h-6 bg-[#22D3EE]/80 rounded-full flex items-center justify-center cursor-pointer hover:bg-[#22D3EE] transition-colors" data-testid={`upload-skill-image-${skill.skill_id}`}>
                          <ImagePlus className="w-3 h-3 text-white" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleSkillImageUpload(skill.skill_id, e)} />
                        </label>
                        <button
                          onClick={(e) => { e.stopPropagation(); onShowImageLibrary(skill.skill_id); }}
                          className="w-6 h-6 bg-[#7C3AED]/70 rounded-full flex items-center justify-center hover:bg-[#7C3AED] transition-colors"
                          data-testid={`library-skill-image-${skill.skill_id}`}
                        >
                          <Image className="w-3 h-3 text-white" />
                        </button>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); removeSkill(skill.skill_id); }} className="absolute -top-1 -left-1 w-5 h-5 bg-red-400 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity z-20">
                        <X className="w-3 h-3 text-white" />
                      </button>
                      <div className="absolute bottom-1 left-1 flex flex-col gap-0.5 z-20">
                        {idx > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); moveSkill(idx, -1); }} className="w-5 h-5 bg-[#1E1B4B]/70 rounded-full flex items-center justify-center hover:bg-[#1E1B4B] transition-colors" data-testid={`move-skill-up-${skill.skill_id}`} title="Move earlier">
                            <ChevronUp className="w-3 h-3 text-white" />
                          </button>
                        )}
                        {idx < session.skills.length - 1 && (
                          <button onClick={(e) => { e.stopPropagation(); moveSkill(idx, 1); }} className="w-5 h-5 bg-[#1E1B4B]/70 rounded-full flex items-center justify-center hover:bg-[#1E1B4B] transition-colors" data-testid={`move-skill-down-${skill.skill_id}`} title="Move later">
                            <ChevronDown className="w-3 h-3 text-white" />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {idx < session.skills.length - 1 && <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
